import type {
  Chord,
  ChordModifier,
  KeyboardLayoutSnapshot,
  Platform,
} from './keybindingTypes';
import { usLayoutAlias } from './usLayoutAlias';

export type KeyboardEventLike = {
  readonly key: string;
  readonly code: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly altKey: boolean;
  readonly shiftKey: boolean;
  readonly location: number;
  readonly isComposing: boolean;
  readonly getModifierState?: (key: string) => boolean;
};

const MODIFIER_ORDER: readonly ChordModifier[] = ['Mod', 'Ctrl', 'Cmd', 'Shift', 'Alt'];
const MODIFIERS = new Set<string>(MODIFIER_ORDER);
const NAMED_KEYS = new Map<string, string>([
  ['enter', 'Enter'], ['escape', 'Escape'], ['space', 'Space'], ['tab', 'Tab'],
  ['backspace', 'Backspace'], ['delete', 'Delete'], ['home', 'Home'], ['end', 'End'],
  ['pageup', 'PageUp'], ['pagedown', 'PageDown'], ['pause', 'Pause'], ['insert', 'Insert'],
  ['arrowleft', 'ArrowLeft'], ['arrowright', 'ArrowRight'],
  ['arrowup', 'ArrowUp'], ['arrowdown', 'ArrowDown'],
]);
const SHIFTED_CODE = /^(Digit\d|Backquote|Minus|Equal|BracketLeft|BracketRight|Backslash|IntlBackslash|IntlRo|IntlYen|Semicolon|Quote|Comma|Period|Slash)$/;
const NUMPAD_CODE = /^Numpad(?:[0-9]|Add|Subtract|Multiply|Divide|Decimal|Equal)$/;
const PRINTABLE_CODE = /^(?:Key[A-Z]|Digit[0-9]|Backquote|Minus|Equal|BracketLeft|BracketRight|Backslash|IntlBackslash|IntlRo|IntlYen|Semicolon|Quote|Comma|Period|Slash)$/;

function normalizePhysicalCode(rawCode: string): { key: string; physical: boolean } | null {
  if (/^F(?:[1-9]|1\d|2[0-4])$/.test(rawCode)) {
    return { key: rawCode, physical: false };
  }
  if (rawCode === 'NumpadEnter') return { key: 'Enter', physical: false };
  if (NUMPAD_CODE.test(rawCode)) return { key: rawCode, physical: false };
  const named = NAMED_KEYS.get(rawCode.toLowerCase());
  if (named) return { key: named, physical: false };
  return PRINTABLE_CODE.test(rawCode) ? { key: rawCode, physical: true } : null;
}

function normalizeKey(rawKey: string): { key: string; physical: boolean } | null {
  const physical = rawKey.match(/^\[([A-Za-z][A-Za-z0-9]*)\]$/);
  if (physical) return normalizePhysicalCode(physical[1]);
  if (/^[a-z]$/i.test(rawKey)) return { key: rawKey.toUpperCase(), physical: false };
  if (/^[0-9]$/.test(rawKey)) return { key: rawKey, physical: false };
  if (/^f(?:[1-9]|1\d|2[0-4])$/i.test(rawKey)) {
    return { key: rawKey.toUpperCase(), physical: false };
  }
  if (NUMPAD_CODE.test(rawKey)) return { key: rawKey, physical: false };
  const named = NAMED_KEYS.get(rawKey.toLowerCase());
  if (named) return { key: named, physical: false };
  if (rawKey === ' ') return { key: 'Space', physical: false };
  if (rawKey.length === 1 && isPrintableAscii(rawKey)) return { key: rawKey, physical: false };
  return null;
}

function splitChord(value: string): { modifiers: string[]; key: string } | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.endsWith('++')) {
    const prefix = trimmed.slice(0, -2);
    return { modifiers: prefix ? prefix.split('+') : [], key: '+' };
  }
  const parts = trimmed.split('+');
  const key = parts.pop();
  return key ? { modifiers: parts, key } : null;
}

function isPrintableToken(chord: Chord): boolean {
  if (chord.physical) return PRINTABLE_CODE.test(chord.key);
  return chord.key.length === 1 || chord.key === 'Space' || chord.key.startsWith('Numpad');
}

function passesTypingGuard(chord: Chord): boolean {
  const modifiers = new Set(chord.modifiers);
  if (modifiers.has('Ctrl') || modifiers.has('Cmd') || modifiers.has('Mod') || modifiers.has('Alt')) return true;
  if (chord.key.match(/^F(?:[1-9]|1\d|2[0-4])$/)) return true;
  return modifiers.has('Shift') && !isPrintableToken(chord);
}

export function parseChord(value: string): Chord | null {
  const split = splitChord(value);
  if (!split) return null;
  const seen = new Set<ChordModifier>();
  for (const rawModifier of split.modifiers) {
    const modifier = MODIFIER_ORDER.find((candidate) => candidate.toLowerCase() === rawModifier.trim().toLowerCase());
    if (!modifier || seen.has(modifier) || !MODIFIERS.has(modifier)) return null;
    seen.add(modifier);
  }
  const normalizedKey = normalizeKey(split.key.trim());
  if (!normalizedKey) return null;
  const chord: Chord = {
    modifiers: MODIFIER_ORDER.filter((modifier) => seen.has(modifier)),
    ...normalizedKey,
  };
  return passesTypingGuard(chord) ? chord : null;
}

export function normalizeChord(value: string | Chord): string | null {
  const chord = typeof value === 'string' ? parseChord(value) : value;
  if (!chord || !passesTypingGuard(chord)) return null;
  const key = chord.physical ? `[${chord.key}]` : chord.key;
  return [...MODIFIER_ORDER.filter((modifier) => chord.modifiers.includes(modifier)), key].join('+');
}

export function formatChord(value: string | Chord, targetPlatform: Platform): string {
  const chord = typeof value === 'string' ? parseChord(value) : value;
  if (!chord) return '';
  const labels: Record<ChordModifier, string> = targetPlatform === 'mac'
    ? { Mod: '⌘', Ctrl: '⌃', Cmd: '⌘', Alt: '⌥', Shift: '⇧' }
    : { Mod: 'Ctrl', Ctrl: 'Ctrl', Cmd: 'Cmd', Alt: 'Alt', Shift: 'Shift' };
  const keyLabels: Record<string, string> = targetPlatform === 'mac'
    ? { Enter: '↩', Escape: 'Esc', Tab: '⇥', Backspace: '⌫', Delete: '⌦', ArrowLeft: '←', ArrowRight: '→', ArrowUp: '↑', ArrowDown: '↓' }
    : {};
  const key = chord.physical
    ? (chord.displayKey ? `${chord.displayKey} [${chord.key}]` : `[${chord.key}]`)
    : (keyLabels[chord.key] ?? chord.key);
  const parts = [...chord.modifiers.map((modifier) => labels[modifier]), key];
  return targetPlatform === 'mac' ? parts.join('') : parts.join('+');
}

export function isPrintableAscii(value: string): boolean {
  return value.length === 1 && value.charCodeAt(0) >= 0x20 && value.charCodeAt(0) <= 0x7e;
}

export function logicalEventKeyToken(value: string): string | null {
  if (!isPrintableAscii(value) && !NAMED_KEYS.has(value.toLowerCase())) return null;
  return normalizeKey(value)?.key ?? null;
}

export function layoutTokenForCode(code: string, layout: KeyboardLayoutSnapshot): string | null {
  if (layout.status !== 'ready') return null;
  const mapped = layout.entries.get(code);
  if (!mapped || !isPrintableAscii(mapped)) return null;
  return normalizeKey(mapped)?.key ?? null;
}

function eventKeyToken(event: KeyboardEventLike, layout: KeyboardLayoutSnapshot): { key: string; physical: boolean; displayKey?: string } | null {
  if (event.code === 'NumpadEnter') return { key: 'Enter', physical: false };
  if (event.location === 3 && NUMPAD_CODE.test(event.code)) return { key: event.code, physical: false };
  const named = event.key === 'Dead' ? null : normalizeKey(event.key);
  const needsLayout = !named || (event.shiftKey && SHIFTED_CODE.test(event.code));
  if (!needsLayout) return named;
  const codeToken = normalizePhysicalCode(event.code);
  if (codeToken && !codeToken.physical) return codeToken;
  if (!PRINTABLE_CODE.test(event.code)) return null;
  const mapped = layoutTokenForCode(event.code, layout);
  if (mapped) return { key: mapped, physical: false };
  if (layout.status === 'loading' || !event.code) return null;
  const layoutLabel = layout.entries.get(event.code);
  const displayKey = layoutLabel && Array.from(layoutLabel).length === 1 ? layoutLabel : undefined;
  return { key: event.code, physical: true, ...(displayKey ? { displayKey } : {}) };
}

function ignoredEvent(event: KeyboardEventLike): boolean {
  return event.isComposing || ['Alt', 'AltGraph', 'Control', 'Meta', 'OS', 'Shift'].includes(event.key) ||
    Boolean(event.getModifierState?.('AltGraph'));
}

export function eventToChord(event: KeyboardEventLike, layout: KeyboardLayoutSnapshot): Chord | null {
  if (ignoredEvent(event)) return null;
  const key = eventKeyToken(event, layout);
  if (!key) return null;
  const modifiers: ChordModifier[] = [];
  if (event.ctrlKey) modifiers.push('Ctrl');
  if (event.metaKey) modifiers.push('Cmd');
  if (event.shiftKey) modifiers.push('Shift');
  if (event.altKey) modifiers.push('Alt');
  const chord: Chord = { modifiers, ...key };
  return passesTypingGuard(chord) ? chord : null;
}

function resolvedModifiers(chord: Chord, targetPlatform: Platform): Set<string> | null {
  if (targetPlatform !== 'mac' && chord.modifiers.includes('Cmd')) return null;
  return new Set(chord.modifiers.map((modifier) => modifier === 'Mod'
    ? (targetPlatform === 'mac' ? 'Cmd' : 'Ctrl')
    : modifier));
}

export function matchesEvent(
  value: string | Chord,
  event: KeyboardEventLike,
  targetPlatform: Platform,
  layout: KeyboardLayoutSnapshot,
): boolean {
  const expected = typeof value === 'string' ? parseChord(value) : value;
  if (!expected || ignoredEvent(event)) return false;
  const expectedModifiers = resolvedModifiers(expected, targetPlatform);
  if (!expectedModifiers) return false;
  const actualModifiers = new Set([
    ...(event.ctrlKey ? ['Ctrl'] : []), ...(event.metaKey ? ['Cmd'] : []),
    ...(event.shiftKey ? ['Shift'] : []), ...(event.altKey ? ['Alt'] : []),
  ]);
  if (expectedModifiers.size !== actualModifiers.size || [...expectedModifiers].some((item) => !actualModifiers.has(item))) return false;
  if (expected.physical) return layout.status !== 'loading' && expected.key === event.code && event.code !== 'NumpadEnter';
  const token = eventKeyToken(event, layout);
  if (!token?.physical) return token?.key === expected.key;
  // FR1: a key the layout cannot name stands for its US-layout key, as in the collision pass.
  return usLayoutAlias(token.key, { shift: event.shiftKey, alt: event.altKey }, targetPlatform, layout) === expected.key;
}
